import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UserIdleService } from 'angular-user-idle';
import { differenceInMinutes, formatISO, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';
import Constants from '../../../assets/consts';
import { MESSAGE_TYPE } from '../constants/message-type';
import { TokenResponse } from '../interfaces/token-reponse';
import { User } from '../interfaces/user';
import { ConfigManagerService } from './config-manager.service';
import { MessageService } from './message.service';
import { ModalService } from './modal.service';
import { SharedStateService } from './shared-state.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #httpClient = inject(HttpClient);
  #configManager = inject(ConfigManagerService);
  #messageService = inject(MessageService);
  #router = inject(Router);
  #userIdleService = inject(UserIdleService);
  #modalService = inject(ModalService);
  #sharedStateService = inject(SharedStateService);
  private timeRemaining = signal<number>(0);
  countdown = computed(() => this.#userIdleService.getConfigValue().timeout - this.timeRemaining());

  init() {
    const LOST_FOCUS_TIME = localStorage.getItem(Constants.BROWSER_LOST_FOCUS_TIMESTAMP) ?? '';
    const FORMATTED_TIME = parseISO(LOST_FOCUS_TIME);
    const TIME_DIFFERENCE = differenceInMinutes(new Date(), FORMATTED_TIME);
    if (TIME_DIFFERENCE >= 35) {
      this.logout(false);
    }

    document.addEventListener('visibilitychange', () => {
      if (this.#sharedStateService.IsLoggedIn()) {
        if (document.visibilityState === 'visible') {
          const LOST_FOCUS_TIME =
            localStorage.getItem(Constants.BROWSER_LOST_FOCUS_TIMESTAMP) ?? '';
          const FORMATTED_TIME = parseISO(LOST_FOCUS_TIME);
          const TIME_DIFFERENCE = differenceInMinutes(new Date(), FORMATTED_TIME);
          if (TIME_DIFFERENCE >= 35) {
            this.#modalService.closeTimeoutModal();
            this.appTimedOut();
          }
        } else {
          const NOW = new Date();
          localStorage.setItem(
            Constants.BROWSER_LOST_FOCUS_TIMESTAMP,
            formatISO(NOW)
          );
        }
      }
    });
  }

  getAuthToken(credentials: {
    username: string;
    password: string;
  }, timeStamp: string): Observable<any> {
    let body = new HttpParams()
      .set('username', encodeURIComponent(credentials.username))
      .set('password', encodeURIComponent(credentials.password))
      .set('grant_type', 'password')
      .set('client_id', this.#configManager.clientId);

    if (timeStamp) {
      body = body.set('timeStamp', encodeURIComponent(timeStamp));
    }

    return this.#httpClient
      .post<TokenResponse>(this.#configManager.tokenUrl, body, {
        headers: new HttpHeaders().set(
          'Content-Type',
          'application/x-www-form-urlencoded;charset=utf-8'
        ),
      })
      .pipe(
        map((response) => {
          const TOKEN = response?.access_token;

          if (TOKEN) {
            this.#sharedStateService.setAuthToken(TOKEN);
          }
        })
      );
  }

  logout(showToast: boolean = true): void {
    this.#sharedStateService.clearDataAfterSession();
    this.#router.navigateByUrl('');
    if (showToast) {
      this.#messageService.showSnackbarMessage({ message: 'Logged Out Successfully!', messageType: MESSAGE_TYPE.Success, allowAutoDismiss: true });
    }
  }

  getUserInfo(): Observable<User | null> {
    return this.#httpClient.get<User>('user').pipe(
      map((userInformation) => {
        this.#sharedStateService.setUser(userInformation);
        if (userInformation.passwordExpired) {
          this.#router.navigate(['account/change-password/expired']);
        }
        this.startTimeOut();
        return userInformation;
      })
    );
  }

  startTimeOut(): void {
    this.#userIdleService.resetTimer();
    this.#userIdleService.startWatching()
    this.#userIdleService.onTimerStart().subscribe((countdown) => {
      this.timeRemaining.set(countdown)
      if (countdown === 1) {
        this.openDialog();
      }
    });

    this.#userIdleService.onTimeout().subscribe((timedOut) => {
      if (timedOut) {
        this.appTimedOut();
      }
    });
  }

  openDialog() {
    return this.#modalService.showTimeoutModal();
  }

  appTimedOut(): void {
    this.#userIdleService.stopTimer();
    this.#userIdleService.stopWatching();
    this.#sharedStateService.clearDataAfterSession();
    this.timeRemaining.set(0);
    this.#router.navigateByUrl('');
    this.#modalService.closeAllModals();
    this.#messageService.showSnackbarMessage({ message: "Your session timed out. Login again to start a new session.", title: "Session Timeout", messageType: MESSAGE_TYPE.Info, allowAutoDismiss: false });
  }
}
